// ---------------------------------------------------------------------------
// DnD Email Editor – Inline rich-text editor
//
// A self-contained contentEditable surface + floating formatting toolbar,
// mounted while a text/html node is being edited. It adopts the proven
// patterns from the FTP HTML editor (FtpCanvas): a saved selection range,
// robust span-based inline styling (no execCommand <font>), a link-insert
// closure, and merge-tag insertion. The editable is UNCONTROLLED after mount
// (its initial HTML is frozen) so model updates never reset the caret; changes
// are flushed OUT via onInput (debounced) and on unmount.
// ---------------------------------------------------------------------------

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ClipboardEvent,
} from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  Unlink,
  RemoveFormatting,
  Braces,
  List,
  ListOrdered,
} from 'lucide-react';
import { hasPageAffectingTags, stripPageAffectingTags } from './dnd-sanitize';
import { WEB_FONTS } from './dnd-webfonts';

const EMAIL_FONTS = [
  // Web fonts carry their own web-safe fallback stack (see dnd-webfonts).
  ...WEB_FONTS.map((f) => f.stack),
  'Arial, Helvetica, sans-serif',
  'Georgia, serif',
  '"Times New Roman", Times, serif',
  '"Courier New", Courier, monospace',
  'Verdana, Geneva, sans-serif',
  'Tahoma, Geneva, sans-serif',
  '"Trebuchet MS", Helvetica, sans-serif',
  '"Lucida Console", Monaco, monospace',
];

const FONT_SIZES = ['10px', '13px', '16px', '18px', '24px', '32px', '48px'];

type Align = 'left' | 'center' | 'right';

export interface InlineTextEditorProps {
  initialHtml: string;
  nodeType: 'text' | 'html';
  align: Align;
  editableStyle: CSSProperties;
  openAtPoint?: { x: number; y: number } | null;
  linkColor: string;
  onInput: (html: string) => void;
  onAlignChange: (align: Align) => void;
  onExit: () => void;
  onOpenLinkModal: (hasSelection: boolean, cb: (url: string, text: string) => void) => void;
  onOpenVariableModal: (cb: (token: string) => void) => void;
}

export function InlineTextEditor({
  initialHtml,
  nodeType,
  align,
  editableStyle,
  openAtPoint,
  linkColor,
  onInput,
  onAlignChange,
  onExit,
  onOpenLinkModal,
  onOpenVariableModal,
}: InlineTextEditorProps) {
  const editableRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  // Frozen once — the editable is uncontrolled after mount so the caret never resets.
  // Document-level tags are dropped: the editable lives in the app's own document,
  // so a <style> carried in the content would restyle the whole page while editing.
  // Editing such a block therefore also normalizes it out of the model on flush,
  // which is intended — a <style> block is not editable content in a WYSIWYG.
  const initialHtmlRef = useRef(stripPageAffectingTags(initialHtml));
  const flushTimer = useRef<number | null>(null);
  const [flipBelow, setFlipBelow] = useState(false);
  const [toolbarLeft, setToolbarLeft] = useState(0); // horizontal shift to stay in-canvas
  const [, force] = useState(0); // re-render for toolbar active states

  // Keep the latest callbacks reachable from stable handlers.
  const onInputRef = useRef(onInput);
  onInputRef.current = onInput;

  // ---- Flush (persist DOM → model) ----------------------------------------

  const flushNow = useCallback(() => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    const el = editableRef.current;
    if (el) onInputRef.current(el.innerHTML);
  }, []);

  const flushSoon = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      const el = editableRef.current;
      if (el) onInputRef.current(el.innerHTML);
    }, 300);
  }, []);

  // Flush any in-progress typing if the node unmounts (drag/delete/mutation).
  useEffect(() => () => { flushNow(); }, [flushNow]);

  // ---- Selection save / restore -------------------------------------------

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  }, []);

  const restoreSelection = useCallback(() => {
    const el = editableRef.current;
    const range = savedRangeRef.current;
    if (!el || !range) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  // ---- Mount: focus + select the double-clicked word ----------------------

  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;

    let range: Range | null = null;
    const pt = openAtPoint;
    if (pt) {
      if (typeof document.caretRangeFromPoint === 'function') {
        range = document.caretRangeFromPoint(pt.x, pt.y);
      } else {
        const anyDoc = document as unknown as {
          caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
        };
        const cp = anyDoc.caretPositionFromPoint?.(pt.x, pt.y);
        if (cp) {
          range = document.createRange();
          range.setStart(cp.offsetNode, cp.offset);
          range.collapse(true);
        }
      }
    }

    if (range && el.contains(range.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(range);
      const modifiable = sel as Selection & {
        modify?: (alter: string, direction: string, granularity: string) => void;
      };
      if (typeof modifiable.modify === 'function') {
        modifiable.modify('move', 'backward', 'word');
        modifiable.modify('extend', 'forward', 'word');
      }
    } else {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Toolbar active-state refresh on selection change -------------------

  useEffect(() => {
    const onSel = () => force((n) => n + 1);
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  // ---- Keep the toolbar inside the canvas ---------------------------------
  // Measured against the nearest scrollable ancestor (the canvas), not the
  // viewport. Vertically: flip below when there's no room above. Horizontally:
  // the toolbar is wide + nowrap and anchored at the block's left edge, so a
  // block near the right (or left) edge clips it — shift it back into view.

  useLayoutEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    let scroll: HTMLElement | null = el.parentElement;
    while (scroll) {
      const oy = getComputedStyle(scroll).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      scroll = scroll.parentElement;
    }
    const scrollRect = scroll
      ? scroll.getBoundingClientRect()
      : { top: 0, left: 0, right: window.innerWidth };
    const editRect = el.getBoundingClientRect();

    const toolbarH = toolbarRef.current?.offsetHeight ?? 40;
    setFlipBelow(editRect.top - scrollRect.top < toolbarH + 8);

    const toolbarW = toolbarRef.current?.offsetWidth ?? 0;
    const pad = 8;
    let shift = 0;
    const rightOverflow = editRect.left + toolbarW - (scrollRect.right - pad);
    if (rightOverflow > 0) shift = -rightOverflow;
    // Never push the left edge past the canvas's left edge.
    if (editRect.left + shift < scrollRect.left + pad) {
      shift = scrollRect.left + pad - editRect.left;
    }
    setToolbarLeft(shift);
  }, []);

  // ---- Commands -----------------------------------------------------------

  // Pasting from a webpage or Word can carry a <style> block along with the
  // markup. Intervene ONLY in that case (sanitize + insert ourselves) so normal
  // paste fidelity is untouched everywhere else.
  const onPaste = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData?.getData('text/html');
    if (!html || !hasPageAffectingTags(html)) return;
    e.preventDefault();
    document.execCommand('insertHTML', false, stripPageAffectingTags(html));
    flushSoon();
  }, [flushSoon]);

  const exec = useCallback((cmd: string, val?: string) => {
    editableRef.current?.focus();
    document.execCommand(cmd, false, val);
    flushSoon();
    force((n) => n + 1);
  }, [flushSoon]);

  const isActive = useCallback((cmd: string) => {
    try { return document.queryCommandState(cmd); } catch { return false; }
  }, []);

  // Wrap the current selection in a styled <span>, then strip the same prop
  // from descendant spans so the outer wins and markup doesn't nest endlessly.
  const applyInlineStyle = useCallback((prop: string, value: string) => {
    const el = editableRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed || !el.contains(range.commonAncestorContainer)) return;

    const span = document.createElement('span');
    (span.style as unknown as Record<string, string>)[prop] = value;
    try {
      range.surroundContents(span);
    } catch {
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);
    }

    span.querySelectorAll('span').forEach((child) => {
      (child.style as unknown as Record<string, string>)[prop] = '';
      if (!child.getAttribute('style')) {
        const parent = child.parentNode;
        if (parent) {
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
        }
      }
    });

    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.removeAllRanges();
    sel.addRange(newRange);
    flushSoon();
    force((n) => n + 1);
  }, [flushSoon]);

  const handleLink = useCallback(() => {
    saveSelection();
    const sel = window.getSelection();
    const hasSelection = !!(sel && !sel.isCollapsed && sel.toString().trim().length > 0);
    const closureRange = savedRangeRef.current ? savedRangeRef.current.cloneRange() : null;
    onOpenLinkModal(hasSelection, (url, text) => {
      const el = editableRef.current;
      if (!el || !closureRange) return;
      el.focus();
      const s = window.getSelection();
      if (!s) return;
      s.removeAllRanges();
      s.addRange(closureRange);
      const anchor = document.createElement('a');
      anchor.href = url;
      if (linkColor) anchor.style.color = linkColor;
      if (hasSelection) {
        anchor.appendChild(closureRange.extractContents());
        closureRange.insertNode(anchor);
      } else {
        anchor.textContent = text || url;
        closureRange.deleteContents();
        closureRange.insertNode(anchor);
      }
      const nr = document.createRange();
      if (hasSelection) {
        nr.selectNodeContents(anchor);
      } else {
        nr.setStartAfter(anchor);
        nr.collapse(true);
      }
      s.removeAllRanges();
      s.addRange(nr);
      flushNow();
    });
  }, [saveSelection, onOpenLinkModal, linkColor, flushNow]);

  const handleVariable = useCallback(() => {
    saveSelection();
    onOpenVariableModal((token) => {
      const el = editableRef.current;
      if (!el) return;
      el.focus();
      restoreSelection();
      document.execCommand('insertText', false, token);
      flushNow();
    });
  }, [saveSelection, restoreSelection, onOpenVariableModal, flushNow]);

  // ---- Toolbar interaction guards -----------------------------------------

  const onToolbarMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    const isNativeControl =
      tag === 'SELECT' ||
      tag === 'OPTION' ||
      (tag === 'INPUT' && (target as HTMLInputElement).type === 'color');
    if (isNativeControl) {
      // Let the native control open, but remember the selection to restore.
      saveSelection();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  }, [saveSelection]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      flushNow();
      onExit();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
      flushSoon();
    }
  }, [flushNow, flushSoon, onExit]);

  const showAlign = nodeType === 'text';

  return (
    <div
      className="dnd-inline-editor"
      style={{ position: 'relative' }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div
        ref={toolbarRef}
        className={`dnd-wysiwyg-toolbar${flipBelow ? ' dnd-wysiwyg-toolbar--below' : ''}`}
        style={{ left: toolbarLeft }}
        onMouseDown={onToolbarMouseDown}
      >
        <button className={isActive('bold') ? 'active' : ''} onClick={() => exec('bold')} title="Bold"><Bold size={13} /></button>
        <button className={isActive('italic') ? 'active' : ''} onClick={() => exec('italic')} title="Italic"><Italic size={13} /></button>
        <button className={isActive('underline') ? 'active' : ''} onClick={() => exec('underline')} title="Underline"><Underline size={13} /></button>
        <button className={isActive('strikeThrough') ? 'active' : ''} onClick={() => exec('strikeThrough')} title="Strikethrough"><Strikethrough size={13} /></button>
        <span className="dnd-tb-sep" />
        <select
          value=""
          onChange={(e) => { restoreSelection(); applyInlineStyle('fontFamily', e.target.value); e.target.value = ''; }}
          title="Font family"
        >
          <option value="" disabled>Font</option>
          {EMAIL_FONTS.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>{f.split(',')[0].replace(/"/g, '')}</option>
          ))}
        </select>
        <select
          value=""
          onChange={(e) => { restoreSelection(); applyInlineStyle('fontSize', e.target.value); e.target.value = ''; }}
          title="Font size"
        >
          <option value="" disabled>Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          type="color"
          title="Text color"
          defaultValue="#333333"
          onChange={(e) => { restoreSelection(); applyInlineStyle('color', e.target.value); }}
        />
        <span className="dnd-tb-sep" />
        <button
          className={isActive('insertUnorderedList') ? 'active' : ''}
          onClick={() => exec('insertUnorderedList')}
          title="Bulleted list"
        >
          <List size={13} />
        </button>
        <button
          className={isActive('insertOrderedList') ? 'active' : ''}
          onClick={() => exec('insertOrderedList')}
          title="Numbered list"
        >
          <ListOrdered size={13} />
        </button>
        {showAlign && (
          <>
            <span className="dnd-tb-sep" />
            <button className={align === 'left' ? 'active' : ''} onClick={() => onAlignChange('left')} title="Align left"><AlignLeft size={13} /></button>
            <button className={align === 'center' ? 'active' : ''} onClick={() => onAlignChange('center')} title="Center"><AlignCenter size={13} /></button>
            <button className={align === 'right' ? 'active' : ''} onClick={() => onAlignChange('right')} title="Align right"><AlignRight size={13} /></button>
          </>
        )}
        <span className="dnd-tb-sep" />
        <button onClick={handleLink} title="Insert link"><Link size={13} /></button>
        <button onClick={() => exec('unlink')} title="Remove link"><Unlink size={13} /></button>
        <button onClick={handleVariable} title="Insert variable"><Braces size={13} /></button>
        <button onClick={() => exec('removeFormat')} title="Clear formatting"><RemoveFormatting size={13} /></button>
      </div>

      <div
        ref={editableRef}
        className="dnd-inline-editable"
        contentEditable
        suppressContentEditableWarning
        data-dnd-editable="true"
        style={editableStyle}
        onInput={flushSoon}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }}
      />
    </div>
  );
}
